import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, ButtonBase, type ButtonBaseProps } from '@mui/material';
import { type ReactNode, useEffect, useRef, useState } from 'react';

/** Hardcoded so the chip stays dark in dark mode (theme text.primary, light). */
const CHIP_BG = '#2a2a2a';
/** Light sage for actions on the dark chip — not a theme token yet. */
const ACTION_TEXT = '#a9d1bb';
const ACTION_TEXT_HOVER = '#c4e3d1';
const DIVIDER = 'rgba(255,255,255,0.18)';
const FONT_SIZE = '13.125px';
const ENTER_MS = 150;

/** The action style, shared by {@link SelectionChipAction} and the rule that
 *  restyles plain buttons a page passes in. */
const actionSx = {
  height: 32,
  minHeight: 32,
  minWidth: 0,
  px: '14px',
  py: 0,
  borderRadius: 999,
  border: 0,
  bgcolor: 'transparent',
  boxShadow: 'none',
  color: ACTION_TEXT,
  fontFamily: 'inherit',
  fontSize: FONT_SIZE,
  fontWeight: 600,
  lineHeight: 1,
  textTransform: 'none',
  letterSpacing: 'normal',
  whiteSpace: 'nowrap',
  '&:hover': {
    bgcolor: 'rgba(169,209,187,0.16)',
    color: ACTION_TEXT_HOVER,
    boxShadow: 'none',
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${ACTION_TEXT}`,
    outlineOffset: 2,
  },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
} as const;

/**
 * A text action on the {@link SelectionChip} ("Combine"). A ButtonBase, not a
 * Button: Button's padding, min-width, uppercase and letter-spacing defaults
 * fight the chip's spec.
 * @param props - ButtonBase props; children is the label
 * @returns The action
 */
export function SelectionChipAction({ sx, ...props }: ButtonBaseProps) {
  return (
    <ButtonBase
      {...props}
      sx={[actionSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    />
  );
}

const Divider = () => (
  <Box
    aria-hidden
    sx={{ width: '1px', height: 18, bgcolor: DIVIDER, flexShrink: 0 }}
  />
);

export interface SelectionChipProps {
  /** Rows selected; the chip shows while this is above zero. */
  count: number;
  /** What the rows are, for "2 jobs selected". */
  noun?: string;
  /** The page's actions — {@link SelectionChipAction}s, ideally. */
  children: ReactNode;
  onClear: () => void;
  /** Hold to the screen (page scroll) instead of the table area. */
  fixed?: boolean;
}

/**
 * The floating bulk-selection bar: a dark pill with the count, the page's
 * actions and a clear button, centred over the table, rising in when rows are
 * selected. Esc clears while it's up.
 * @param props - See {@link SelectionChipProps}
 * @returns The chip
 */
export function SelectionChip({
  count,
  noun = 'items',
  children,
  onClear,
  fixed = false,
}: SelectionChipProps) {
  const open = count > 0;
  // Keep showing the last count while it animates out.
  const lastCount = useRef(count);
  if (open) lastCount.current = count;
  const shown = open ? count : lastCount.current;

  // Mounted from open until the exit animation ends; `entered` drives it.
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(false);
    const t = setTimeout(() => setMounted(false), ENTER_MS);
    return () => clearTimeout(t);
  }, [open]);

  const clearRef = useRef(onClear);
  clearRef.current = onClear;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!mounted) return null;
  const label = `${shown} ${noun} selected`;

  return (
    <Box
      sx={{
        position: fixed ? 'fixed' : 'absolute',
        left: '50%',
        // Over the table's own area: 24px above it (clearing the pager below
        // it); on a page-scrolled phone, held to the screen's bottom edge.
        bottom: fixed
          ? 'calc(16px + env(safe-area-inset-bottom, 0px))'
          : { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', sm: 72 },
        transform: `translate(-50%, ${entered ? 0 : 8}px)`,
        opacity: entered ? 1 : 0,
        transition: `opacity ${ENTER_MS}ms ease-out, transform ${ENTER_MS}ms ease-out`,
        zIndex: (t) => t.zIndex.speedDial,
        maxWidth: 'calc(100% - 16px)',
      }}
    >
      <Box
        role="toolbar"
        aria-label="Bulk actions"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          height: 40,
          pl: '8px',
          pr: '4px',
          bgcolor: CHIP_BG,
          color: '#fff',
          borderRadius: 999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
          fontSize: FONT_SIZE,
          // Plain buttons a page passes in take the action style too.
          '& .MuiButton-root': actionSx,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { mr: 0.5 },
          '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.75)' },
        }}
      >
        <Box aria-live="polite" sx={{ display: 'contents' }}>
          <Box
            aria-label={label}
            title={label}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 24,
              height: 24,
              px: '7px',
              boxSizing: 'border-box',
              borderRadius: 999,
              bgcolor: 'primary.main',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              mr: '6px',
              flexShrink: 0,
            }}
          >
            {shown}
          </Box>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {children}
        </Box>

        <Divider />

        <ButtonBase
          onClick={onClear}
          aria-label="Clear selection"
          sx={{
            width: 32,
            height: 32,
            ml: '2px',
            p: 0,
            borderRadius: 999,
            flexShrink: 0,
            color: 'rgba(255,255,255,0.75)',
            '& .inner': { transition: 'background-color 120ms' },
            '&:hover': { color: '#fff' },
            '&:hover .inner': { bgcolor: 'rgba(255,255,255,0.18)' },
            '&.Mui-focusVisible': {
              outline: `2px solid ${ACTION_TEXT}`,
              outlineOffset: 2,
            },
          }}
        >
          <Box
            className="inner"
            sx={{
              width: 24,
              height: 24,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.10)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </Box>
        </ButtonBase>
      </Box>
    </Box>
  );
}
